import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, Heading, Text } from "@lendcord/concord";
import { useApproveApplicationMutation } from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";

interface ApprovalStepProps {
  applicationId: string;
  isEditAllowed: boolean;
}

// The approval screen for applicationTypes whose journey (see
// applicationJourney.ts) has no loan terms or CRIF check — LENDER/STAFF/
// AGENT land here directly once OTP verification completes (state
// VERIFIED, which is also their approval gate). Same approve action as
// CrifStep's, just without any of the CRIF score/report UI since there's no
// credit check for this applicationType.
export function ApprovalStep({ applicationId, isEditAllowed }: ApprovalStepProps) {
  const intl = useIntl();
  const [approveApplication, { isLoading: isApproving }] = useApproveApplicationMutation();
  const [approveError, setApproveError] = useState<string | undefined>();

  async function handleApprove() {
    setApproveError(undefined);
    try {
      await approveApplication(applicationId).unwrap();
    } catch (error) {
      setApproveError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.review.crifApproveError" }),
      );
    }
  }

  return (
    <div className="mt-6 flex flex-col gap-4 rounded-md border border-border bg-surface-raised p-6">
      <div>
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.approvalHeading" />
        </Heading>
        <Text as="p" size="sm" variant="muted" className="mt-1">
          <FormattedMessage id="lender.onboarding.review.approvalDescription" />
        </Text>
      </div>

      {approveError && <AlertBanner status="error">{approveError}</AlertBanner>}

      {isEditAllowed && (
        <div>
          <Button type="button" onClick={handleApprove} disabled={isApproving}>
            {isApproving ? (
              <FormattedMessage id="lender.onboarding.review.crifApprovingButton" />
            ) : (
              <FormattedMessage id="lender.onboarding.review.crifApproveButton" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
